import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  OrderAssignmentConfigService,
  OrderAssignmentConfiguration,
} from "../../../../../api/services/orderAssignmentConfigService";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import {
  AGENTIC_MEDICAL_ORDER_STATUSES,
  HUMAN_MEDICAL_ORDER_STATUSES,
} from "../utils/medicalOrderStatusConstants";
import {
  deepClone,
  filterOrderAssignmentConfigurationsByProviderSearch,
  hasConfigurationChanged,
  isWithinBlockedTimeWindow,
} from "../utils/orderAssignmentHelpers";
import {
  generateOrderAssignmentTableData,
  OrderAssignmentTableColumnKeys,
} from "../utils/orderAssignmentTable";

interface UseOrderAssignmentConfigurationsProps {
  selectedFacilityId: string;
  /** Current table page start index (from CustomTable startIndexOfTable). Used to convert page-local rowIndex to global index. */
  tableStartIndex?: number;
  /** Search string from the table; drives visible rows and row-index resolution. */
  searchTerm: string;
  onEdit?: (config: OrderAssignmentConfiguration) => void;
}

interface UseOrderAssignmentConfigurationsReturn {
  configurations: OrderAssignmentConfiguration[];
  originalConfigurations: OrderAssignmentConfiguration[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
  loadConfigurations: () => Promise<void>;
  handleSave: () => Promise<void>;
  handleDiscard: () => void;
  handleToggleChange: (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => void;
  handleTableRowUpdate: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  handleConfigurationUpdate: (
    configId: string,
    updates: {
      auth_status?: string[];
      payer_patterns?: string[];
    },
  ) => void;
  /** Row payloads for `CustomTable` (search-filtered; same order as row indices). */
  tableData: ReturnType<typeof generateOrderAssignmentTableData>;
  /** Count of rows after applying `searchTerm` (for pagination). */
  filteredConfigurationCount: number;
}

export const useOrderAssignmentConfigurations = ({
  selectedFacilityId,
  tableStartIndex = 0,
  searchTerm,
  onEdit,
}: UseOrderAssignmentConfigurationsProps): UseOrderAssignmentConfigurationsReturn => {
  const [configurations, setConfigurations] = useState<
    OrderAssignmentConfiguration[]
  >([]);
  const [originalConfigurations, setOriginalConfigurations] = useState<
    OrderAssignmentConfiguration[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch configurations for the selected facility
   */
  const loadConfigurations = useCallback(async () => {
    if (!selectedFacilityId) {
      setConfigurations([]);
      setOriginalConfigurations([]);
      setHasUnsavedChanges(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data =
        await OrderAssignmentConfigService.getConfigurationsByFacilityId(
          selectedFacilityId,
        );
      setConfigurations(data);
      setOriginalConfigurations(deepClone(data));
      setHasUnsavedChanges(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch configurations";
      setError(errorMessage);
      toast.error("Failed to load order assignment configurations");
      setConfigurations([]);
      setOriginalConfigurations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId]);

  /**
   * Handle toggle change - updates local state only, doesn't save to Firestore
   */
  const handleToggleChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    setConfigurations((prev) =>
      prev.map((config) =>
        config.id === data.name
          ? { ...config, working_today: data.value }
          : config,
      ),
    );
    setHasUnsavedChanges(true);
  };

  /**
   * Helper function to update a configuration field by configId
   */
  const updateConfigField = useCallback(
    (configId: string, updates: Partial<OrderAssignmentConfiguration>) => {
      setConfigurations((prev) =>
        prev.map((config) =>
          config.id === configId ? { ...config, ...updates } : config,
        ),
      );
      setHasUnsavedChanges(true);
    },
    [],
  );

  /**
   * Configurations shown in the table after search (single source for visible rows).
   */
  const visibleConfigurations = useMemo(
    () =>
      filterOrderAssignmentConfigurationsByProviderSearch(
        configurations,
        searchTerm,
      ),
    [configurations, searchTerm],
  );

  /**
   * Table rows for `CustomTable` and for resolving row index → config on cell edit.
   */
  const tableData = useMemo(
    () =>
      generateOrderAssignmentTableData(visibleConfigurations, {
        onEdit: onEdit,
      }),
    [visibleConfigurations, onEdit],
  );

  const filteredConfigurationCount = visibleConfigurations.length;

  /**
   * Handle table row update - updates local state when cells are edited.
   * CustomTable passes page-local rowIndex (0-based on current page). We convert to
   * global index using tableStartIndex so we update the correct config.
   */
  const handleTableRowUpdate = useCallback(
    (rowIndex: number, fieldKey: string, newValue: any) => {
      const globalRowIndex = tableStartIndex + rowIndex;
      if (globalRowIndex < 0 || globalRowIndex >= tableData.length) {
        return;
      }

      const rowData = tableData[globalRowIndex]?.rowData;
      if (!rowData?.id) {
        return;
      }

      const configId = rowData.id;
      let updates: Partial<OrderAssignmentConfiguration> = {};

      // Handle Multi Agent column update
      if (fieldKey === OrderAssignmentTableColumnKeys.IfMultiRegimen) {
        const isMultiAgent = newValue?.currentStatusId === "yes";
        updates = { if_multi_regimen: isMultiAgent };
      }
      // Handle Medical Order Status column update
      else if (fieldKey === OrderAssignmentTableColumnKeys.MedicalOrderStatus) {
        const typeValue = newValue?.currentStatusId || "human";
        // If agentic, save: ["order_completed_by_agent", "order_in_progress"]
        // If human, save: ["yet_to_start_work_on_order"]
        const medicalOrderStatus =
          typeValue === "agentic"
            ? AGENTIC_MEDICAL_ORDER_STATUSES
            : HUMAN_MEDICAL_ORDER_STATUSES;
        updates = { medical_order_status: medicalOrderStatus };
      }
      // Handle Count column update
      else if (fieldKey === OrderAssignmentTableColumnKeys.Capacity) {
        const capacityValue =
          typeof newValue === "string"
            ? parseInt(newValue, 10) || 0
            : typeof newValue === "number"
              ? newValue
              : 0;
        updates = { capacity: capacityValue };
      }

      if (Object.keys(updates).length > 0) {
        updateConfigField(configId, updates);
      }
    },
    [tableData, tableStartIndex, updateConfigField],
  );

  /**
   * Save all changed configurations to Firestore
   */
  const handleSave = async () => {
    if (!selectedFacilityId || configurations.length === 0) return;

    // Check if current time is within blocked window (9:30 AM - 1:00 PM)
    if (isWithinBlockedTimeWindow()) {
      toast.error("Changes not allowed between 9:30AM to 1:00PM");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Find all configurations that have changed
      const changedConfigs = configurations.filter((config) => {
        if (!config.id) return false;
        const original = originalConfigurations.find(
          (orig) => orig.id === config.id,
        );
        return original ? hasConfigurationChanged(original, config) : false;
      });

      if (changedConfigs.length === 0) {
        setHasUnsavedChanges(false);
        return;
      }

      // Update all changed configurations in Firestore
      await Promise.all(
        changedConfigs.map((config) => {
          const { id, ...fieldsToUpdate } = config;
          return OrderAssignmentConfigService.updateConfiguration(
            id!,
            fieldsToUpdate,
          );
        }),
      );

      // Update original configurations to match current state
      setOriginalConfigurations(deepClone(configurations));
      setHasUnsavedChanges(false);
      toast.success(
        ToastMessages.OPTION_SAVE_ALL_SUCCESS ||
          "Order assignment configurations saved successfully",
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save configurations";
      setError(errorMessage);
      toast.error("Failed to save order assignment configurations");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Discard all changes and revert to original configurations
   */
  const handleDiscard = () => {
    setConfigurations(deepClone(originalConfigurations));
    setHasUnsavedChanges(false);
  };

  /**
   * Handle configuration update from modal - updates local state only
   */
  const handleConfigurationUpdate = (
    configId: string,
    updates: {
      auth_status?: string[];
      payer_patterns?: string[];
    },
  ) => {
    // Find the original configuration to compare against
    const originalConfig = originalConfigurations.find(
      (orig) => orig.id === configId,
    );

    // Check if the updates actually change the configuration
    const hasActualChanges =
      !originalConfig ||
      (updates.auth_status !== undefined &&
        JSON.stringify(originalConfig.auth_status) !==
          JSON.stringify(updates.auth_status)) ||
      (updates.payer_patterns !== undefined &&
        JSON.stringify(originalConfig.payer_patterns) !==
          JSON.stringify(updates.payer_patterns));

    setConfigurations((prev) =>
      prev.map((config) =>
        config.id === configId
          ? {
              ...config,
              ...(updates.auth_status !== undefined && {
                auth_status: updates.auth_status,
              }),
              ...(updates.payer_patterns !== undefined && {
                payer_patterns: updates.payer_patterns,
              }),
            }
          : config,
      ),
    );

    // Only set hasUnsavedChanges if there are actual changes
    if (hasActualChanges) {
      setHasUnsavedChanges(true);
    }
  };

  // Load configurations when selectedFacilityId changes
  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  return {
    configurations,
    originalConfigurations,
    loading,
    saving,
    error,
    hasUnsavedChanges,
    loadConfigurations,
    handleSave,
    handleDiscard,
    handleToggleChange,
    handleTableRowUpdate,
    handleConfigurationUpdate,
    tableData,
    filteredConfigurationCount,
  };
};
